import { Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiClientAssessmentVersioningService } from 'src/app/api/api-client-assessment-versioning.service';
import { AssessmentHistoryVersion, AssessmentResourceId } from 'src/app/api/models/assessments/assessment.interface';
import { AssessmentPage } from '../models/assessments.interface';

export interface GroupedAssessmentHistory {
    today: AssessmentHistoryVersion[];
    yesterday: AssessmentHistoryVersion[];
    older: AssessmentHistoryVersion[];
}

@Injectable({
    providedIn: 'root'
})
export class AssessmentsVersioningService {
    
    isVersionHistoryOpen = signal<boolean>(false);

    constructor(
        private apiClientAssessmentVersioningService: ApiClientAssessmentVersioningService
    ) {}

    private readonly MaxRecords = 10;
    
    getAssessmentVersionById(assessmentPageId: AssessmentResourceId, versionId: string): Observable<AssessmentPage> {
        return this.apiClientAssessmentVersioningService.getAssessmentVersionById(assessmentPageId, versionId);
    }
    
    restoreAssessmentVersion(assessmentPageId: AssessmentResourceId, versionId: string): Observable<AssessmentPage> {
        return this.apiClientAssessmentVersioningService.restoreAssessmentVersion(assessmentPageId, versionId);
    }

    getAssessmentVersionHistory(assessmentPageId: AssessmentResourceId): Observable<AssessmentHistoryVersion[]> {
        return this.apiClientAssessmentVersioningService.getAssessmentVersionHistory(assessmentPageId);
    }

    getGroupedAssessmentVersionHistory(assessmentPageId: AssessmentResourceId, maxRecords: number = this.MaxRecords): Observable<GroupedAssessmentHistory> {
        return this.getAssessmentVersionHistory(assessmentPageId).pipe(
            map((history: AssessmentHistoryVersion[]) => {
                const now = new Date();
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);

                const grouped: GroupedAssessmentHistory = {
                    today: [],
                    yesterday: [],
                    older: []
                };

                // Sort history by date (most recent first)
                const sortedHistory = [...history].sort((a, b) => 
                    new Date(b.historyUpdatedAt).getTime() - new Date(a.historyUpdatedAt).getTime()
                );

                const limitedHistory = maxRecords ? sortedHistory.slice(0, maxRecords) : sortedHistory;

                limitedHistory.forEach(item => {
                    const itemDate = new Date(item.historyUpdatedAt);
                    const itemDateOnly = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());

                    if (itemDateOnly.getTime() === today.getTime()) {
                        grouped.today.push(item);
                    } else if (itemDateOnly.getTime() === yesterday.getTime()) {
                        grouped.yesterday.push(item);
                    } else {
                        grouped.older.push(item);
                    }
                });

                return grouped;
            })
        );
    }
}